import { Badge } from '@atlas/ui-primitives'
import { ExternalLink } from 'lucide-react'
import { Eyebrow } from '@/components/editorial/PageHeader'
import type { WorkOrder, WorkOrderLink } from '@/domain/work-order.types'
import { statusLabel } from '@/domain/work-order.types'
import { formatDateTime } from '@/lib/format-date'

type ExternalFacts = Pick<
	WorkOrder,
	| 'source'
	| 'externalRef'
	| 'externalReferenceNumber'
	| 'externalStatus'
	| 'externalStatusLabel'
	| 'externalOwnerName'
	| 'externalOwnerEmail'
	| 'externalSyncedAt'
	| 'links'
>

/** The external system's name as the page shows it: `workfront` → "Workfront". */
export const sourceName = (source: string) => statusLabel(source)

/** The order's link into the external system: the related link named after it, if any. */
const externalLinkOf = (order: ExternalFacts): WorkOrderLink | undefined =>
	order.links.find((link) => link.name.toLowerCase() === order.source.toLowerCase())

/**
 * What the external system says about the order: its number and status, when ATLAS last
 * applied a version from it, a link to it, and its owner there. Only an order with an external
 * reference has one.
 */
export function ExternalSection({ order }: { order: ExternalFacts }) {
	if (!order.externalRef) return null
	const name = sourceName(order.source)
	const link = externalLinkOf(order)
	return (
		<section aria-label={name} className="mt-6">
			<Eyebrow className="mb-2">{name}</Eyebrow>
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span className="font-mono font-medium text-ds-text">
					#{order.externalReferenceNumber ?? order.externalRef}
				</span>
				{order.externalStatus ? (
					<Badge size="sm" variant="secondary" title={order.externalStatus}>
						{order.externalStatusLabel ?? order.externalStatus}
					</Badge>
				) : null}
				{order.externalSyncedAt ? (
					<span className="text-xs text-ds-text-muted">
						Last synced from {name}{' '}
						<time dateTime={order.externalSyncedAt}>{formatDateTime(order.externalSyncedAt)}</time>
					</span>
				) : null}
				{link ? (
					<a
						href={link.url}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 text-ds-primary hover:underline"
					>
						Open in {name}
						<ExternalLink className="h-3 w-3" aria-hidden />
					</a>
				) : null}
			</div>
			<dl className="mt-2 space-y-1 text-xs text-ds-text-muted">
				{order.externalOwnerName ? (
					<div>
						<dt className="inline">Owner: </dt>
						<dd className="inline">
							{order.externalOwnerName}
							{order.externalOwnerEmail ? ` · ${order.externalOwnerEmail}` : ''}
						</dd>
					</div>
				) : null}
			</dl>
		</section>
	)
}
